'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { authApi } from '../api/auth-api';
import { toast } from '@/hooks/use-toast';

export function useLogin(options?: { skipRedirect?: boolean }) {
  const router = useRouter();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.login,
    onSuccess: data => {
      // Invalidate and refetch user data to ensure UI updates
      queryClient.setQueryData(['user'], data.user);
      queryClient.invalidateQueries({ queryKey: ['user'] });
      
      // Only redirect if not in checkout context
      if (!options?.skipRedirect) {
        router.push('/');
      }
      
      toast({
        title: 'Welcome back!',
        description: `Signed in as ${data.user.email}`,
      });
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: 'Oops!',
        description: error.response?.data?.message || 'Login failed',
      });
    },
  });
}

export function useRegister() {
  const router = useRouter();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.register,
    onSuccess: data => {
      queryClient.setQueryData(['user'], data.user);
      router.push('/');
      toast({
        title: 'Welcome!',
        description: `Account created successfully`,
      });
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Oops!',
        description: error.response?.data?.message || 'Registration failed',
      });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const router = useRouter();

  const clearUserState = () => {
    // Clear all user-related data from React Query
    queryClient.setQueryData(['user'], null);
    queryClient.removeQueries({ queryKey: ['user'] });
    queryClient.clear();
    
    // Redirect to home page after a short delay to ensure cookies are cleared
    setTimeout(() => {
      router.push('/');
      // Force a page refresh to ensure clean state
      window.location.reload();
    }, 100);
  };

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      clearUserState();
      toast({
        title: 'Signed out',
        description: 'You have been signed out',
      });
    },
    onError: (error: any) => {
      // Even if logout fails on server, clear client state
      clearUserState();
      toast({
        variant: 'destructive',
        title: 'Signed out',
        description: 'You have been signed out (offline)',
      });
    },
  });
}
